import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
  IconButton,
  Collapse,
  Autocomplete,
  InputAdornment,
  Tooltip,
  Badge,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  ExpandMore,
  ExpandLess,
  Search as SearchIcon,
} from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImagePopUp from "./grnpopup/ImagePopUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { externalgetApi, externalImagepostApi } from "../../services/externalApis";
import {
  SETTING_API_01,
  SETTING_API_03,
  SETTING_API_09,
  SETTING_API_05,
  SETTING_API_17,
  UPLOAD_API,
} from "../../api/ApiModel";
import { ENDPOINT } from "../../api/EndPoints";
import { useSelector } from "react-redux";
import { CON_STATUS_ACTIVE, CON_MODULECODE } from "../../utils/sys/Constants";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Upload from "../common/Upload";
import {
  allowOnlyNumbers,
  allowOnlyNumbersCommaDot,
  FormatamountWithoutDecimal,
  FormatCurrencyLKR,
  formatCurrencyForInput,
  parseCurrencyFromInput,
} from "../../utils/common/CurrencyFormatter";
import { isImageFile } from "../../utils/grn/fileUtils";
import SystemAlert from "../common/SystemAlert";
import {
  ITEM_LABELS,
  TABLE_HEADERS,
  BUTTON_LABELS,
  PLACEHOLDERS,
  BILLING_LABELS,
  GRN_FORM_LABELS,
  GRN_SECTIONS,
} from "../../utils/sys/GrnLabels";
import {
  GRN_CONSTRAINTS,
  GRN_DATE_FORMAT,
  REASON_CATEGORY,
  GRN_REGEX,
  DOC_CATEGORY,
  TAX_CODE,
  DOC_CATEGORY_CODE,
} from "../../utils/sys/GrnConstants";
import { CURRENCY_CONFIG } from "../../utils/sys/GrnConfig";
import { AnyForNow, GrnData, GrnItem, GrnItemsTableProps } from "../../features/grn/types";
import { GRN_ERROR_MESSAGES } from "../../utils/sys/GrnValidation";

/** Returns URL to use for attachment src: data/absolute URLs as-is; relative paths get API base prepended; base64 gets correct data URL. */
function getAttachmentDisplaySrc(value: string): string {
  if (!value || typeof value !== "string") return "";
  if (value.startsWith("data:") || value.startsWith("http://") || value.startsWith("https://")) return value;
  const base = ENDPOINT.replace(/\/api\/?$/, "");
  return base + (value.startsWith("/") ? value : "/" + value);
}

function getAttachmentSrc(attachment: AnyForNow, mimeKind: "image" | "pdf" | "octet"): string {
  const raw = attachment?.FILEPATH || attachment?.fileUrl || attachment?.docpathjson || "";
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("http")) return raw;
  const isBase64 = raw.length > 100 && /^[A-Za-z0-9+/=]+$/.test(raw);
  if (isBase64) {
    const prefix = mimeKind === "image" ? "data:image/jpeg;base64," : mimeKind === "pdf" ? "data:application/pdf;base64," : "data:application/octet-stream;base64,";
    return prefix + raw;
  }
  return getAttachmentDisplaySrc(raw);
}

const GrnItemsTable: React.FC<GrnItemsTableProps> = ({
  openSidebarToBillingInfo,
  showRejectItems,
  showFocItems,
  showItemAttachments,
  setReasonForReje,
  toggleRejectItems,
  toggleFocItems,
  toggleItemAttachments,
  supplierItems,
  grnItems,
  inputValue,
  setInputValue,
  newItem,
  addItemToTable,
  handleItemChange,
  errors,
  setErrors,
  removeItemFromTable,
  validateItem,
  setGrnItems,
  setNewItem,
  setSearchInput,
  handleClearAttachments,
  handleClearFocItems,
  handleClearRejectItems,
  grnData,
  onAddItem,
  payTax
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [showGrnItems, setShowGrnItems] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<boolean[]>([]);
  const [imagePopupOpen, setImagePopupOpen] = useState<boolean>(false);
  const [popupImage, setPopupImage] = useState<string | null>(null);
  const [popupName, setPopupName] = useState<string>("");
  const [popupDescription, setPopupDescription] = useState<string>("");
  const [grnDocTypes, setGrnDocTypes] = useState<AnyForNow[]>([]);
  const [taxrateEdited, setTaxrateEdited] = React.useState<Record<string, boolean>>({});
  const [imagePreviewOpen, setImagePreviewOpen] = React.useState<boolean>(false);
  const [selectedImage, setSelectedImage] = React.useState<AnyForNow | null>(null);
  const [documentType, setDocumentType] = useState<string>("");
  const [doctypeothername, setDoctypeothername] = useState<string>("");
  const [searchTableInput, setSearchTableInput] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [attachmentFileName, setAttachmentFileName] = useState<string>("");
  const [rejectFileName, setRejectFileName] = useState<string>("");
  const [brandList, setBrandList] = useState<AnyForNow[]>([]);
  const [mouList, setMouList] = useState<AnyForNow[]>([]);
  const [reasonOptions, setReasonOptions] = useState<AnyForNow[]>([]);
  const [reasonLoading, setReasonLoading] = useState<boolean>(false);
  const [rejectAttachmentUploadingIndex, setRejectAttachmentUploadingIndex] = useState<number | null>(null);
  const [itemAttachmentUploadingIndex, setItemAttachmentUploadingIndex] = useState<number | null>(null);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"error" | "success" | "warning" | "info">("error");
  const [alertMessage, setAlertMessage] = useState<string>("");

  // Get user parameters from Redux store
  const SYS_USER = useSelector((state: AnyForNow) => state.user) || {};
  const { COMPCODE, STAFFMAPLOCODE } = SYS_USER.USER_PARAM || {};

  // Calculate gross amount directly from grnItems to avoid one-render delay
  const calculatedGrossAmount = useMemo(() => {
    if (!Array.isArray(grnItems) || grnItems.length === 0) return 0;
    return grnItems.reduce((sum, item) => sum + (Number(item.itemvalue) || 0), 0);
  }, [grnItems]);

  const [expandedAdditional, setExpandedAdditional] = React.useState<{ [key: number]: boolean }>({});

const handleExpandAdditionalClick = (index: number) => {
  setExpandedAdditional((prev) => ({ ...prev, [index]: !prev[index] }));
};

  const handleRejectItemChange = (index: number, field: string, value: any): void => {
    setGrnItems((prev) => {
      const updated = [...prev];

      // Update rejectItem field
      updated[index].rejectItem = {
        ...updated[index].rejectItem,
        [field]: value,
      };

      // Recalculate acceptedqty based on receivedqty - rejectItem.returnedqty
      const receivedQty = Number(updated[index].receivedqty) || 0;
      const rejectedQty =
        Number(updated[index].rejectItem.returnedqty) || 0;
      const freeQty = Number(updated[index].freeqty) || 0;
      const acceptedQty = receivedQty - rejectedQty;
      updated[index].acceptedqty = acceptedQty >= 0 ? acceptedQty : 0;

      // Recalculate quantity difference
      const poQty = Number(updated[index].poqty) || 0;
      updated[index].quantitydifference = poQty - updated[index].acceptedqty;

      // Keep dependent quantities in sync
      const billQty = Math.max(acceptedQty - freeQty, 0);
      updated[index].billqty = billQty;
      updated[index].totalqty = acceptedQty + freeQty;
      updated[index].pendingpoqty = Math.max(poQty - acceptedQty, 0);

      // Recalculate Value and Tax Value when returnedqty changes
      // This ensures the Value field updates correctly when reject items are added
      if (field === "returnedqty") {
        const currentAcceptedQty = acceptedQty >= 0 ? acceptedQty : 0;
        const currentUnitPrice = Number(updated[index].unitprice) || 0;
        const currentDiscountRate = Number(updated[index].discountrate) || 0;
        const currentTaxRate = Number(updated[index].taxrate) || 0;

        // Calculate item value: acceptedQty * unitPrice
        const itemValue = currentAcceptedQty * currentUnitPrice;
        
        // Calculate discount amount
        const discountAmount = (itemValue * currentDiscountRate) / 100;
        
        // Calculate value after discount
        const grossAfterDiscount = itemValue - discountAmount;
        
        // Calculate tax value on the amount after discount
        const taxValue = (grossAfterDiscount * currentTaxRate) / 100;

        // Update the item with calculated values
        updated[index].grossvalue = parseFloat(itemValue.toFixed(2));
        updated[index].itemvalue = parseFloat(itemValue.toFixed(2)); // Value field displays itemvalue
        updated[index].taxvalue = parseFloat(taxValue.toFixed(2));
        updated[index].discountvalue = parseFloat(discountAmount.toFixed(2));
      }

      return updated;
    });
  };

  // const handleRejectAttachmentAdd = (index, attachment) => {
  //   setGrnItems((prev) => {
  //     const updated = [...prev];
  //     if (!updated[index].rejectItem) updated[index].rejectItem = {};
  //     if (!Array.isArray(updated[index].rejectItem.attachments)) {
  //       updated[index].rejectItem.attachments = [];
  //     }
  //     updated[index].rejectItem.attachments.push(attachment);
  //     return updated;
  //   });
  // };

// With item-wise state:
const [expandedOptional, setExpandedOptional] = React.useState<{ [key: number]: boolean }>({});

const toggleOptionalFields = (index: number): void => {
  setExpandedOptional((prev) => ({
    ...prev,
    [index]: !prev[index],
  }));
};

  // const handleExpandClick = (index: number): void => {
  //   setExpanded((prev) => {
  //     const newExpanded = prev.map(() => false);
  //     newExpanded[index] = true;
  //     return newExpanded;
  //   });
  // };

  const handleRejectAttachmentAdd = (index: number, attachment: AnyForNow): void => {
    const uniqueId = `${Date.now()}-${Math.random()}`;
    const attachmentWithId = { ...attachment, id: uniqueId };

    setGrnItems((prev) => {
      const updated = [...prev];
      if (!updated[index].rejectItem) updated[index].rejectItem = {};
      // Allow only one document: replace any existing with the new one
      updated[index].rejectItem.attachments = [attachmentWithId];
      // Also update docpathjson to keep it in sync with attachments array
      // This ensures the attachment is visible when navigating to ViewDetailGrn
      updated[index].rejectItem.docpathjson = JSON.stringify([attachmentWithId]);
      return updated;
    });
  };

  const handleRejectAttachmentRemove = (itemIndex: number, attachmentId: string): void => {
    setGrnItems((prev) => {
      // Map over all items
      return prev.map((item) => {
        if (!item.rejectItem) return item;

        // Remove from attachments array
        const updatedAttachments = (item.rejectItem.attachments || []).filter(
          (att: AnyForNow) => att.id !== attachmentId
        );

        // Remove from existing attachments docpathjson
        let existing: AnyForNow[] = [];
        try {
          existing = item.rejectItem.docpathjson
            ? JSON.parse(item.rejectItem.docpathjson)
            : [];
        } catch (e) {
          console.error("Failed to parse existing docpathjson", e);
        }

        const updatedExisting = existing.filter(
          (att: AnyForNow) => att.id !== attachmentId
        );

        return {
          ...item,
          rejectItem: {
            ...item.rejectItem,
            attachments: updatedAttachments,
            docpathjson: JSON.stringify(updatedExisting),
          },
        };
      });
    });
    // Reset file name state to allow re-uploading
    setRejectFileName("");
  };

  useEffect(() => {
    const fetchReasons = async (): Promise<void> => {
      // Only fetch if both COMPCODE and STAFFMAPLOCODE are available
      if (!COMPCODE || !STAFFMAPLOCODE) {
        console.warn("Cannot fetch reason options: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
        setReasonOptions([]);
        setReasonLoading(false);
        return;
      }

      setReasonLoading(true);
      
      try {
        const params = {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          MODULECODE: CON_MODULECODE,
          REASONCATCODE: "14",
          STS: CON_STATUS_ACTIVE,
        };
        
        console.log("Fetching Reasontype with params:", params);
        console.log("Full URL would be:", `${ENDPOINT}${SETTING_API_17}?${new URLSearchParams(params as any).toString()}`);
        
        const result = await externalgetApi(SETTING_API_17, "get", params);
        
        console.log("Raw API result for Reasontype:", result);
        console.log("Result type:", typeof result);
        console.log("Is array:", Array.isArray(result));
        console.log("Result keys:", result && typeof result === 'object' ? Object.keys(result) : 'N/A');
        
        // Handle different response formats (array or object)
        let options = [];
        if (Array.isArray(result)) {
          options = result;
        } else if (result && typeof result === 'object' && !Array.isArray(result)) {
          // Convert object with numeric keys to array
          const values = Object.values(result);
          options = values.filter(item => item && typeof item === 'object');
          // If no objects found, try treating the object itself as an array of values
          if (options.length === 0 && values.length > 0) {
            options = values;
          }
        } else if (result) {
          // If result is truthy but not array/object, wrap it
          options = [result];
        }
        
        console.log("Processed reason options:", options);
        console.log("Number of options:", options.length);
        
        if (options.length > 0) {
          console.log("First option sample:", options[0]);
        }
        
        setReasonOptions(options);
      } catch (error: any) {
        console.error("Error fetching reason options:", error);
        console.error("Error response:", error.response);
        console.error("Error details:", error.response?.data || error.message);
        setReasonOptions([]);
      } finally {
        setReasonLoading(false);
      }
    };

    fetchReasons();
  }, [COMPCODE, STAFFMAPLOCODE]);

  // const handleItemAttachmentRemove = (index, attachmentIndex) => {
  //   setGrnItems((prev) => {
  //     const updated = [...prev];

  //     if (
  //       !updated[index].itemAttachments ||
  //       !Array.isArray(updated[index].itemAttachments.attachments)
  //     ) {
  //       return updated;
  //     }

  //     updated[index].itemAttachments.attachments.splice(attachmentIndex, 1);
  //     return updated;
  //   });
  // };

  const handleItemAttachmentRemove = (itemIndex: number, attachmentToRemove: AnyForNow): void => {
    setGrnItems((prev) => {
      const updated = [...prev];

      let item = updated[itemIndex];
      let attachmentsArray: AnyForNow[] = [];

      // Extract existing attachments from itemAttachments
      if (Array.isArray(item.itemAttachments)) {
        attachmentsArray = [...item.itemAttachments];
      } else if (typeof item.itemAttachments === "object") {
        if (Array.isArray(item.itemAttachments.attachments)) {
          attachmentsArray = [...item.itemAttachments.attachments];
        } else {
          attachmentsArray = [item.itemAttachments];
        }
      }

      // Preserve metadata (remark, document type, custom document type) from the attachment being removed
      // or from the current attachment structure before removal
      const attachmentToPreserve = attachmentsArray.find(
        (att: AnyForNow) =>
          att === attachmentToRemove ||
          att.filename === attachmentToRemove.filename ||
          att.FILENAME === attachmentToRemove.FILENAME ||
          att.FILEPATH === attachmentToRemove.FILEPATH
      ) || (attachmentsArray.length > 0 ? attachmentsArray[0] : null);

      const preservedRemark = attachmentToPreserve?.remark || 
                               attachmentToPreserve?.REMARK || 
                               ((item.itemAttachments as any)?.attachments?.[0]?.remark) ||
                               ((item.itemAttachments as any)?.attachments?.[0]?.REMARK) ||
                               "";
      const preservedDoctypeCode = attachmentToPreserve?.doctypecode || 
                                   attachmentToPreserve?.DOCTYPECODE ||
                                   item.selectedDocumentTypeCode ||
                                   ((item.itemAttachments as any)?.attachments?.[0]?.doctypecode) ||
                                   "";
      const preservedDoctype = attachmentToPreserve?.doctype || 
                                attachmentToPreserve?.DOCTYPE ||
                                ((item.itemAttachments as any)?.attachments?.[0]?.doctype) ||
                                "";
      const preservedDoctypeOtherName = attachmentToPreserve?.doctypeothername || 
                                        attachmentToPreserve?.DOCTYPEOTHERNAME ||
                                        item.doctypeothername ||
                                        ((item.itemAttachments as any)?.attachments?.[0]?.doctypeothername) ||
                                        "";

      // Filter out the attachment to be removed (match by filename, fileUrl, etc.)
      attachmentsArray = attachmentsArray.filter(
        (att: AnyForNow) =>
          att !== attachmentToRemove &&
          att.filename !== attachmentToRemove.filename &&
          att.FILENAME !== attachmentToRemove.FILENAME &&
          att.FILEPATH !== attachmentToRemove.FILEPATH
      );

      // When all files are removed, preserve Document Type and Remark but clear file-related fields
      if (attachmentsArray.length === 0) {
        // Keep the preserved metadata values (Document Type and Remark)
        item.selectedDocumentTypeCode = preservedDoctypeCode;
        item.doctypeothername = preservedDoctypeOtherName;
        item.itemAttachments = {
          attachments: [{
            remark: preservedRemark,
            doctypecode: preservedDoctypeCode,
            doctype: preservedDoctype,
            doctypeothername: preservedDoctypeOtherName,
            // Clear only file-related fields
            docpathjson: "",
            filename: "",
            bytarr: "",
            fileUrl: "",
            FILEPATH: "",
            objectUrl: "",
          }],
          doctypecode: preservedDoctypeCode,
          doctypeothername: preservedDoctypeOtherName,
          docpathjson: "",
          filename: "",
          bytarr: "",
          remark: preservedRemark,
        };
        return updated;
      }

      // Preserve metadata at item level
      if (preservedDoctypeCode) {
        item.selectedDocumentTypeCode = preservedDoctypeCode;
      }
      if (preservedDoctypeOtherName) {
        item.doctypeothername = preservedDoctypeOtherName;
      }

      // Assign updated attachments back, preserving metadata structure
      if (item.itemAttachments && !Array.isArray(item.itemAttachments) && 'attachments' in item.itemAttachments) {
        // case: nested attachments
        item.itemAttachments.attachments = attachmentsArray;
        // If no attachments remain, preserve metadata in an empty attachment object
        if (attachmentsArray.length === 0 && (preservedRemark || preservedDoctypeCode || preservedDoctypeOtherName)) {
          item.itemAttachments.attachments = [{
            remark: preservedRemark,
            doctypecode: preservedDoctypeCode,
            doctype: preservedDoctype,
            doctypeothername: preservedDoctypeOtherName,
            docpathjson: "", // Required property, empty since no file
            filename: "", // Required property, empty since no file
            bytarr: "", // Required property, empty since no file
          }];
        }
      } else if (Array.isArray(item.itemAttachments)) {
        // case: top-level array - convert to nested structure when preserving metadata
        if (attachmentsArray.length === 0 && (preservedRemark || preservedDoctypeCode || preservedDoctypeOtherName)) {
          // Convert to nested structure to preserve metadata
          item.itemAttachments = {
            attachments: [{
              remark: preservedRemark,
              doctypecode: preservedDoctypeCode,
              doctype: preservedDoctype,
              doctypeothername: preservedDoctypeOtherName,
              docpathjson: "", // Required property, empty since no file
              filename: "", // Required property, empty since no file
              bytarr: "", // Required property, empty since no file
            }]
          };
        } else {
          item.itemAttachments = attachmentsArray;
        }
      } else {
        // case: single attachment converted to array
        if (attachmentsArray.length === 0 && (preservedRemark || preservedDoctypeCode || preservedDoctypeOtherName)) {
          // Preserve metadata in an empty attachment object
          item.itemAttachments = {
            attachments: [{
              remark: preservedRemark,
              doctypecode: preservedDoctypeCode,
              doctype: preservedDoctype,
              doctypeothername: preservedDoctypeOtherName,
              docpathjson: "", // Required property, empty since no file
              filename: "", // Required property, empty since no file
              bytarr: "", // Required property, empty since no file
            }]
          };
        } else {
          item.itemAttachments = attachmentsArray.length === 1 ? attachmentsArray[0] : attachmentsArray;
        }
      }

      return updated;
    });
  };

  const handleItemAttachmentChange = (index: number, field: string, value: any): void => {
    const updatedItems = [...grnItems];
    const attachment =
      (updatedItems[index].itemAttachments && !Array.isArray(updatedItems[index].itemAttachments) && (updatedItems[index].itemAttachments as any).attachments?.[0]) || {};

    updatedItems[index].itemAttachments = {
      ...(updatedItems[index].itemAttachments as any),
      attachments: [
        {
          ...attachment,
          [field]: value, // dynamically update remark or other fields
        },
      ],
    };

    setGrnItems(updatedItems);
  };

  const getMouNameByCode = (code: string | number): string | null => {
    if (!code) return null;
    const mou = mouList.find((b: AnyForNow) => b.immeasuringunitcode === code);
    return mou?.immeasuringunit || null;
  };

  const getBrandNameByCode = (code: string | number): string | null => {
    if (!code) return null;
    const brand = brandList.find((b: AnyForNow) => b.brandcode === code);
    return brand?.brandname || null;
  };

  // table item count
  const totalItemCount = grnItems.length;

  // search items in the table
  // IMPORTANT: Use grnItems directly instead of grnData.products to avoid
  // circular updates. The TextFields show values from grnItems and handleItemChange
  // also updates grnItems. Using grnData.products caused a mismatch and infinite loop.
  const filteredTableItems = useMemo(() => {
    const itemList = Array.isArray(grnItems) ? grnItems : [];
    const searchText = (searchTableInput || "").toString().toLowerCase();

    if (!searchText) {
      return itemList;
    }

    return itemList.filter((item: AnyForNow) => {
      const productName = (item?.productname || "").toString().toLowerCase();
      const productCode = (item?.productcode || "").toString().toLowerCase();
      const brandName = (item?.brandname || "").toString().toLowerCase();

      return (
        productName.includes(searchText) ||
        productCode.includes(searchText) ||
        brandName.includes(searchText)
      );
    });
  }, [grnItems, searchTableInput]);

  // console.log("grnData?.products", grnData?.products);
const handleExpandClick = (index: number): void => {
  setExpanded((prev) => {
    const newExpanded = prev.map(() => false);
    newExpanded[index] = true;
    return newExpanded;
  });

  // Reset all optional fields when switching rows
  setExpandedOptional({});
};
  // Track previous grnItems length to avoid unnecessary state updates
  const prevGrnItemsLengthRef = React.useRef<number>(0);

  useEffect(() => {
    const currentLength = grnItems.length;
    const prevLength = prevGrnItemsLengthRef.current;

    // Only update expanded state if length actually changed
    if (currentLength !== prevLength) {
      prevGrnItemsLengthRef.current = currentLength;

      setExpanded((prevExpanded) => {
        // Case 1: New row added
        if (currentLength > prevExpanded.length) {
          const newExpanded = Array(currentLength).fill(false);
          newExpanded[0] = true; // expand only the first row (newly added)
          return newExpanded;
        }

        // Case 2: Rows removed - trim existing state
        if (currentLength < prevExpanded.length) {
          return prevExpanded.slice(0, currentLength);
        }

        // Case 3: Same length - keep existing state
        return prevExpanded;
      });
    }
  }, [grnItems.length]); // Only depend on length, not entire array

  const toggleShowGrnItems = (): void => {
    setShowGrnItems((prev) => !prev);
  };

  // Refs to prevent duplicate API calls
  const hasFetchedBrandsRef = React.useRef(false);
  const hasFetchedMouRef = React.useRef(false);
  const hasFetchedDocCategoryRef = React.useRef(false);

const handleAddItem = (index: number): void => {
  // Optionally validate item before adding
  const item = grnItems[index];
  const errors = validateItem(item);
  if (Object.keys(errors).length > 0) {
    setErrors((prev: AnyForNow) => ({ ...prev, [index]: errors }));
    return;
  }

  setGrnItems((prev) =>
    prev.map((item, idx) =>
      idx === index ? { ...item, isAdded: true } : item
    )
  );

  // Clear errors for this item
  setErrors((prev: AnyForNow) => {
    const newErrors = { ...prev };
    delete newErrors[index];
    return newErrors;
  });

  // Collapse the row after adding
  setExpanded((prev) => {
    const newExpanded = [...prev];
    newExpanded[index] = false;
    return newExpanded;
  });

  // Reset additional fields expansion for this index so the next
  // item added at the same index does not inherit the open state
  setExpandedOptional((prev) => {
    const next = { ...prev };
    delete next[index];
    return next;
  });

  resetItemInputStates();

  // Call the processGrn API
  if (onAddItem) {
    onAddItem();
  }
};

  const resetItemInputStates = (): void => {
    setInputValue("");
    setSearchInput("");
    setReasonForReje("");
    setFileName("No file selected");
    setNewItem(null);
  };

  // useEffect(() => {
  //   setShowRejectItems((prev) => {
  //     if (prev.length === grnItems.length) return prev;
  //     return Array(grnItems.length).fill(false);
  //   });
  //   setShowFocItems((prev) => {
  //     if (prev.length === grnItems.length) return prev;
  //     return Array(grnItems.length).fill(false);
  //   });
  //   setShowItemAttachments((prev) => {
  //     if (prev.length === grnItems.length) return prev;
  //     return Array(grnItems.length).fill(false);
  //   });
  // }, [grnItems,]);

  const uploadAttachmentFile = async (rawFile: File | null): Promise<string> => {
    if (!rawFile) return "";
    const formData = new FormData();
    formData.append("file", rawFile);
    const response = await externalImagepostApi(UPLOAD_API, formData);
    return response?.filepath || response?.filePath || response?.FILEPATH || "";
  };

  const handleItemAttachmentFileUpload = (file: AnyForNow, index: number): void => {
    if (!Array.isArray(grnItems) || index < 0 || index >= grnItems.length) {
      console.error("Invalid index or grnItems not properly defined", {
        index,
        grnItems,
      });
      return;
    }

    const updatedItems = [...grnItems];
    // Preserve remark from current attachment or from item-level preserved values
    const currentRemark =
      ((updatedItems[index].itemAttachments as any)?.attachments?.[0]?.remark) ||
      ((updatedItems[index].itemAttachments as any)?.attachments?.[0]?.REMARK) ||
      "";

    // Get document type code from file parameter or from item-level preserved values
    const doctypeCode = file.doctypecode || 
                        updatedItems[index].selectedDocumentTypeCode ||
                        ((updatedItems[index].itemAttachments as any)?.attachments?.[0]?.doctypecode) ||
                        "";

    // Get the document type name from the code
    const selectedDocType = grnDocTypes.find((doc: AnyForNow) => doc.doctypecode === doctypeCode);
    const doctypeName = selectedDocType?.doctype || 
                        ((updatedItems[index].itemAttachments as any)?.attachments?.[0]?.doctype) ||
                        "";

    // Get custom document type from file parameter or from item-level preserved values
    const doctypeOtherName = file.doctypeothername || 
                             updatedItems[index].doctypeothername ||
                             ((updatedItems[index].itemAttachments as any)?.attachments?.[0]?.doctypeothername) ||
                             "";

    // When upload path exists, store only path (no base64) so state/localStorage stay small and navigation works.
    const usePathOnly = Boolean(file?.fileUrl && String(file.fileUrl).trim());
    updatedItems[index].itemAttachments = {
      ...updatedItems[index].itemAttachments,
      attachments: [
        {
          docpathjson: usePathOnly ? String(file.fileUrl).trim() : (file?.base64 || ""),
          filename: file?.fileName || "",
          bytarr: usePathOnly ? [] : (file?.byteArray || []),
          objectUrl: file?.objectUrl || "",
          fileUrl: file?.fileUrl || "",
          FILEPATH: file?.fileUrl || "",
          doctype: doctypeName, // document type name from selected code
          doctypecode: doctypeCode, // document type code from file or preserved value
          doctypeothername: doctypeOtherName,
          remark: currentRemark, // preserve remark even when file is removed
        },
      ],
    };

    // Also update item-level values to keep them in sync
    if (doctypeCode) {
      updatedItems[index].selectedDocumentTypeCode = doctypeCode;
    }
    if (doctypeOtherName) {
      updatedItems[index].doctypeothername = doctypeOtherName;
    }

    setGrnItems(updatedItems);
  };

  const fetchBrands = React.useCallback(async (): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) {
      return;
    }

    // Prevent duplicate calls
    if (hasFetchedBrandsRef.current) {
      return;
    }

    try {
      hasFetchedBrandsRef.current = true;
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(SETTING_API_01, "GET", params);
      setBrandList(response || []);
    } catch (error: any) {
      // Handle 404 errors gracefully - endpoint might not exist
      if (error?.response?.status === 404) {
        // Silently handle 404 - endpoint may not be available
        console.warn("Brands API endpoint not found (404). Continuing without brands data.");
        setBrandList([]);
      } else {
        // Log other errors but don't break the app
        console.error("Error fetching brands:", error);
        setBrandList([]);
      }
      // Don't reset ref on 404 - we don't want to retry a non-existent endpoint
      if (error?.response?.status !== 404) {
        hasFetchedBrandsRef.current = false; // Reset on other errors to allow retry
      }
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  const fetchMou = React.useCallback(async (): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) {
      return;
    }

    // Prevent duplicate calls
    if (hasFetchedMouRef.current) {
      return;
    }

    try {
      hasFetchedMouRef.current = true;
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(SETTING_API_05, "GET", params);
      setMouList(response || []);
    } catch (error: any) {
      // Handle 404 errors gracefully - endpoint might not exist
      if (error?.response?.status === 404) {
        // Silently handle 404 - endpoint may not be available
        console.warn("MOU API endpoint not found (404). Continuing without MOU data.");
        setMouList([]);
      } else {
        // Log other errors but don't break the app
        console.error("Error fetching MOU:", error);
        setMouList([]);
      }
      // Don't reset ref on 404 - we don't want to retry a non-existent endpoint
      if (error?.response?.status !== 404) {
        hasFetchedMouRef.current = false; // Reset on other errors to allow retry
      }
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Fetch GRN item-based document types using a fixed DOCCATCODE, matching the
  // previously working API call:
  // Doctype?COMPCODE=...&LOCCODE=...&DOCCATCODE=1&STS=A
  const fetchGrnDocType = React.useCallback(async (): Promise<void> => {
    if (!COMPCODE || !STAFFMAPLOCODE) {
      console.warn("Cannot fetch GRN document types: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
      return;
    }

    try {
      const params = {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        DOCCATCODE: DOC_CATEGORY_CODE.ITEM_BASED,
        STS: CON_STATUS_ACTIVE,
      };
      console.log("Fetching GRN item-based document types with params:", params);
      const response = await externalgetApi(SETTING_API_03, "GET", params);
      const docTypes = Array.isArray(response) ? response : [];
      console.log("Fetched GRN item-based document types:", docTypes);
      setGrnDocTypes(docTypes); // Save doc types to state
    } catch (error) {
      console.error("Error fetching GRN document types", error);
      setGrnDocTypes([]);
    }
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Backwards-compatible wrapper kept for minimal changes to the rest of the code.
  // Instead of discovering the category via Doccategory API, we now directly call
  // Doctype with the known DOCCATCODE used prior to the revamp.
  const fetchGrnDocCategory = React.useCallback(async (): Promise<void> => {
    // Prevent duplicate calls
    if (hasFetchedDocCategoryRef.current) {
      console.log("Document category already fetched, skipping...");
      return;
    }

    hasFetchedDocCategoryRef.current = true;
    await fetchGrnDocType();
  }, [fetchGrnDocType]);

  // Initial fetch - only once when component mounts
  useEffect(() => {
    if (COMPCODE && STAFFMAPLOCODE) {
      console.log("Initializing GRN Items Table - fetching data...", { COMPCODE, STAFFMAPLOCODE });
      fetchBrands();
      fetchMou();
      fetchGrnDocCategory();
    } else {
      console.warn("Cannot initialize GRN Items Table: COMPCODE or STAFFMAPLOCODE missing", { COMPCODE, STAFFMAPLOCODE });
    }
  }, [fetchBrands, fetchMou, fetchGrnDocCategory, COMPCODE, STAFFMAPLOCODE]);

  // console.log("ITEMMMMMMMM",grnItems);
  //Tax types

  const goToAddNewItem = React.forwardRef<HTMLDivElement, AnyForNow>((props, ref) => {
    const { children, ...other } = props;
    return (
      <Paper ref={ref} {...other} elevation={8}>
        {children}
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${(theme.palette as any).greyscale.stroke_1}`,
          }}
        >
          {/* <Button
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            sx={{
              ...styles.btn_text,
              textAlign: "left",
              justifyContent: "left",
            }}
            // onClick={handleAddNewSupplier}
            fullWidth
          >
            New Item
          </Button> */}
        </Box>
      </Paper>
    );
  });
  
  goToAddNewItem.displayName = 'GoToAddNewItem';

  return (
    <>
      <Box>
        <Grid container>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              onClick={toggleShowGrnItems}
            >
              <IconButton
                sx={{
                  transform: showGrnItems ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                  mr: 1,
                }}
              >
                <ExpandMoreOutlinedIcon />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography sx={styles.grn_sub_title_text}>
                  {ITEM_LABELS.GRN_ITEMS}
                </Typography>
                <Badge
                  color="secondary"
                  badgeContent={totalItemCount.toFixed(0)}
                ></Badge>
              </Box>
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                alignContent: "center",
                width: "35%",
                justifyContent: "flex-end",
                gap: 1,
                mr: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "right",
                  width: "100%",
                  borderRight: `2px solid ${(theme.palette as any).greyscale.stroke_1}`,
                  pr: 2,
                  mb: "2px",
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: (theme.typography as any).body_xs,
                  }}
                >
                  {BILLING_LABELS.GROSS_AMOUNT}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                    }}
                    onClick={openSidebarToBillingInfo}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        openSidebarToBillingInfo();
                      }
                    }}
                  >
                    <Tooltip title="View Full Billing Info">
                      <InfoOutlinedIcon
                        sx={{ color: "system.submit", fontSize: "20px" }}
                      />
                    </Tooltip>
                  </Box>
                  <Typography
                    sx={{
                      color: theme.palette.secondary.main,
                      fontSize: (theme.typography as any).subtitle_s,
                      fontWeight: "bold",
                      textShadow: 10,
                    }}
                  >
                    {CURRENCY_CONFIG.CODE}{" "}
                    {FormatCurrencyLKR(
                      calculatedGrossAmount > 0
                        ? calculatedGrossAmount.toFixed(2)
                        : CURRENCY_CONFIG.DEFAULT_VALUE
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "70%",
                justifyContent: "flex-end",
              }}
            >
              {/* Table list item search */}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder={ITEM_LABELS.SEARCH_PLACEHOLDER}
                value={searchTableInput}
                onChange={(e) => setSearchTableInput(e.target.value)}
                sx={styles.ipt_appbar_search}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton sx={styles.btn_appbar_search_icon}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <IconButton aria-label="delete">
                <QrCodeScannerOutlinedIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Divider />
      </Box>

      <Collapse in={showGrnItems} timeout="auto" unmountOnExit>
        <TableContainer
          component={Paper}
          sx={{ ...styles.grn_table_container }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ ...styles.grn_table_header_row }}>
                <TableCell
                  sx={{
                    maxWidth: "42px",
                    padding: 0,
                  }}
                ></TableCell>
                <TableCell sx={styles.grn_table_header_col_2}>
                  <Typography
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      textAlign: "left",

                      borderRight: `1px solid ${theme.palette.divider}`,
                      ml: 2,
                      my: 1,
                    }}
                    variant="subtitle_s"
                  >
                    {ITEM_LABELS.ITEM_DETAILS}
                  </Typography>
                </TableCell>

                <TableCell sx={styles.grn_table_header_col_2}>
                  <Typography
                    sx={styles.grn_table_header_col_box_2}
                    variant="subtitle_s"
                  >
                    {ITEM_LABELS.DATE_MEASUREMENTS}
                  </Typography>
                </TableCell>

                <TableCell sx={styles.grn_table_header_col_2}>
                  <Typography
                    variant="subtitle_s"
                    sx={{
                      ...styles.grn_table_header_col_box_2,

                      justifyContent: "flex-end",
                      textAlign: "right",
                      pr: 2,
                    }}
                  >
                    {ITEM_LABELS.QUANTITY}
                  </Typography>
                </TableCell>

                <TableCell sx={styles.grn_table_header_col_2}>
                  <Typography
                    variant="subtitle_s"
                    sx={{
                      ...styles.grn_table_header_col_box_2,

                      justifyContent: "flex-end",
                      textAlign: "right",
                      pr: 2,
                    }}
                  >
                    {ITEM_LABELS.AMOUNT_LKR}
                  </Typography>
                </TableCell>

                <TableCell
                  sx={{
                    maxWidth: "50px",
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Table row goes to here */}
              <TableRow>
                <TableCell sx={styles.grn_table_row_expand}>
                  <Box sx={styles.grn_table_row_expand_container}>
                    {/* Disabled expand icon for add row */}
                    <IconButton size="small" disabled>
                      <ExpandMore />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell sx={styles.common_cell_style_2_search}>
                  <Box sx={{ ...styles.grn_table_body_col_box, pr: 0 }}>
                    <Autocomplete
                      freeSolo
                      fullWidth
                      // disableClearable
                      options={supplierItems}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option?.productname || "";
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        return (
                          option?.productcode === value?.productcode ||
                          option?.id === value?.id ||
                          option?.supproductcode === value?.supproductcode
                        );
                      }}
                      value={newItem}
                      inputValue={inputValue}
                      onInputChange={(e, newInputValue, reason) => {
                        if (reason !== 'reset') {
                          setInputValue(newInputValue);
                        }
                      }}
                      onChange={(event, newValue, reason) => {
                        if (typeof newValue === 'string') {
                          setNewItem(null);
                          setInputValue(newValue);
                          return;
                        }
                        if (!newValue) {
                          setNewItem(null);
                          setInputValue("");
                          return;
                        }
                        if (newValue && typeof newValue === 'object' && (newValue.productcode || newValue.id || newValue.supproductcode)) {
                          addItemToTable(newValue);
                          setTimeout(() => {
                            setNewItem(null);
                            setInputValue("");
                          }, 100);
                        } else {
                          setNewItem(null);
                          setInputValue("");
                        }
                      }}
                      filterOptions={(options, params) => {
                        if (!params.inputValue) {
                          return options;
                        }
                        const filtered = options.filter((option) => {
                          const inputValue = params.inputValue.toLowerCase();
                          return option?.productname?.toLowerCase().includes(inputValue);
                        });
                        return filtered;
                      }}
                      noOptionsText="No items found"
                      loadingText="Loading items..."
                      openOnFocus={true}
                      selectOnFocus={false}
                      clearOnBlur={false}
                      handleHomeEndKeys={true}
                      slotProps={{
                        paper: {
                          elevation: 8,
                          sx: {
                            maxHeight: '300px',
                          },
                        },
                        listbox: {
                          style: {
                            maxHeight: '300px',
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Click or type to find an item."
                          size="small"
                          fullWidth
                          onKeyDown={(e) => {
                            // Allow Enter key to work naturally with Autocomplete
                          }}
                        />
                      )}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={styles.common_cell_style_3}></TableCell>
                <TableCell sx={styles.common_cell_style_3}></TableCell>
                <TableCell sx={styles.common_cell_style_3} align="right">
                  
                </TableCell>
              
<TableCell sx={styles.common_cell_style_2_action}>
 
</TableCell>
              </TableRow>
              {/* main row */}
              {filteredTableItems.map((item: AnyForNow, index: number) => {
                // console.log("filteredTableItems", filteredTableItems);

                const rejectItem = item.rejectItem;
                
                // Parse rejectItem attachments - handle both array and docpathjson string
                let rejectAttachmentsParsed: AnyForNow[] = [];
                if (rejectItem?.attachments) {
                  // If attachments is already an array, use it directly
                  rejectAttachmentsParsed = Array.isArray(rejectItem.attachments) 
                    ? rejectItem.attachments 
                    : [];
                } else if (rejectItem?.docpathjson) {
                  // If docpathjson exists, try to parse it
                  try {
                    const parsed = typeof rejectItem.docpathjson === 'string' 
                      ? JSON.parse(rejectItem.docpathjson) 
                      : rejectItem.docpathjson;
                    rejectAttachmentsParsed = Array.isArray(parsed) ? parsed : [];
                  } catch (e) {
                    console.error("Failed to parse rejectItem.docpathjson:", e);
                    rejectAttachmentsParsed = [];
                  }
                }

                let itemAttachmentsArray: AnyForNow[] = [];

                try {
                  const raw = item?.itemAttachments;

                  if (Array.isArray(raw)) {
                    itemAttachmentsArray = raw;
                  } else if (typeof raw === "string") {
                    const parsed = JSON.parse(raw);
                    itemAttachmentsArray = Array.isArray(parsed)
                      ? parsed
                      : [parsed];
                  } else if (typeof raw === "object" && raw !== null) {
                    // Check if it has an `attachments` array
                    if (Array.isArray(raw.attachments)) {
                      itemAttachmentsArray = raw.attachments;
                    } else {
                      itemAttachmentsArray = [raw];
                    }
                  }
                } catch (e) {
                  console.error("Failed to parse itemAttachments:", e);
                  itemAttachmentsArray = [];
                }

                const validAttachments = itemAttachmentsArray.filter(
                  (att: AnyForNow) =>
                    att?.docpathjson ||
                    att?.fileUrl ||
                    att?.FILEPATH ||
                    att?.filename
                  // att?.remark
                );
                // console.log("itemAttachmentsArray", itemAttachmentsArray);

                // Filter reject attachments to only include those with valid file data
                const attachmentsToShow = rejectAttachmentsParsed.filter((att: AnyForNow) => {
                  // Only include attachments that have valid file data
                  const hasValidFileData = 
                    (att?.docpathjson && att.docpathjson.trim() !== "") ||
                    (att?.fileUrl && att.fileUrl.trim() !== "") ||
                    (att?.FILEPATH && att.FILEPATH.trim() !== "") ||
                    (att?.objectUrl && att.objectUrl.trim() !== "");
                  
                  // Also check for filename if it's a saved attachment from server
                  const hasFileName = att?.filename || att?.FILENAME;
                  
                  // Include if it has valid file data OR filename (for saved attachments)
                  return hasValidFileData || hasFileName;
                });

                return (
                  <React.Fragment key={(item.id, index)}>
                    {/* <Box>          <Button
  variant="text"
  size="small"
  onClick={() => toggleOptionalFields(index)}   // ← Pass index
  sx={styles.btn_text}
>
  {expandedOptional[index]
    ? GRN_SECTIONS.HIDE_ADDITIONAL
    : GRN_SECTIONS.ADDITIONAL_DETAILS}
</Button></Box> */}
                                    <TableRow>
                      <TableCell sx={styles.grn_table_expand_cell} colSpan={7}>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box>
                            <Grid
                              container
                              spacing={2}
                              sx={styles.grn_table_expand_cell_grid}
                            >
                              <Grid
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 12,
                                  lg: 12,
                                  xl: 12,
                                }}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
  variant="text"
  size="small"
  onClick={() => toggleOptionalFields(index)}   // ← Pass index
  sx={styles.btn_text}
>
  {expandedOptional[index]
    ? GRN_SECTIONS.HIDE_ADDITIONAL
    : GRN_SECTIONS.ADDITIONAL_DETAILS}
</Button>
                              </Grid>
                            </Grid>
                          </Box>
                          <Divider />
                        </Collapse>
                      </TableCell>
                    </TableRow>
      

                    <TableRow
                      key={item.id}
                      sx={{
                        backgroundColor: expanded[index]
                          ? theme.palette.background.bgShade_3
                          : "inherit",
                      }}
                    >
                      {/* 1st */}
                      <TableCell sx={styles.grn_table_row_expand}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignContent: "center",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "1px",
                          }}
                        >
                          <Box
                            sx={{
                              ...styles.grn_table_row_expand_container,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleExpandClick(index)}
                            >
                              {expanded[index] ? (
                                <ExpandLess />
                              ) : (
                                <ExpandMore />
                              )}
                            </IconButton>{" "}
                          </Box>
                          <CircleIcon
                            sx={{
                              fontSize: "10px",
                              color: item.isAdded
                                ? "system.success"
                                : "system.error",
                            }}
                          />
                        </Box>
                      </TableCell>
                      {/* 2nd item details*/}
                      <TableCell sx={styles.common_cell_style_3}>
                        <Box sx={styles.grn_table_body_col_box_2} mb={0}>
                          <Box sx={styles.grn_table_body_col_box_row}>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              label={ITEM_LABELS.ITEM_NAME}
                              required
                              value={item.productname}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "productname",
                                  e.target.value
                                )
                              }
                              disabled
                              // sx={styles.txt_grn_input_3}
                            />
                          </Box>
                        </Box>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={styles.grn_cell_collapse_box}>
                            <Box sx={styles.grn_table_body_col_box_row}>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label={ITEM_LABELS.ITEM_CODE}
                                value={item.productcode}
                                disabled
                              />
                            </Box>
                            <Box sx={styles.grn_table_body_col_box_row}>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label={ITEM_LABELS.BRAND}
                                value={
                                  getBrandNameByCode(item.brandcode) ??
                                  item.brandname ??
                                  "No-MOU"
                                }
                                disabled
                              />
                            </Box>
                           <Box sx={{ display: "flex", gap: 1 }}>
                             
                            <TextField
  variant="outlined"
  size="small"
  fullWidth
  label={ITEM_LABELS.ITEM_DES}
  value={item.productdescrip}
 disabled
/>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>

                      {/* 3th unit price */}
                      <TableCell sx={styles.common_cell_style_3}>
                        <Box sx={styles.grn_table_body_col_box_2} mb={0}>
                          <Box sx={styles.grn_table_body_col_box_row} gap={4}>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              label={ITEM_LABELS.UOM}
                              required
                              value={
                                getMouNameByCode(item.immeasuringunitcode) ??
                                item.immeasuringunit ??
                                "No-MOU"
                              }
                              disabled
                              // sx={styles.txt_grn_input_3}
                            />
                          </Box>
                        </Box>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={styles.grn_cell_collapse_box}>
                            <TextField
                              variant="outlined"
                              size="small"
                              label={ITEM_LABELS.SIZE}
                              value={item.productsize}
                              disabled
                              fullWidth
                            />
                            <Box sx={styles.grn_table_body_col_box_row}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={ITEM_LABELS.MFD}
                                  format={GRN_DATE_FORMAT.SETUP}
                                 
                                   value={
                                    item.manufacdate &&
                                    item.manufacdate !== GRN_DATE_FORMAT.DEFAULT_WITH_TIME // Check for the specific "empty" date
                                      ? dayjs(item.manufacdate) // If it's a valid date, show it formatted
                                      : null // If it's the fallback default date, hide it by setting to null
                                  }
                                  onChange={(newDate) => {
                                    handleItemChange(
                                      index,
                                      "manufacdate",
                                      newDate
                                        ? newDate.format(GRN_DATE_FORMAT.STANDARD)
                                        : null
                                    );
                                  }}
                                  sx={styles.ipt_helper_text}
                                  maxDate={dayjs().subtract(0, "day")}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      size: "small",
                                   
                                   
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>

                            <Box sx={styles.grn_table_body_col_box_row}>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                  label={ITEM_LABELS.EXP}
                                  // value={
                                  //   item.expdate ? dayjs(item.expdate) : null
                                  // }
                                  format={GRN_DATE_FORMAT.SETUP}
                                  value={
                                    item.expdate &&
                                    item.expdate !== GRN_DATE_FORMAT.DEFAULT_WITH_TIME // Check for the specific "empty" date
                                      ? dayjs(item.expdate) // If it's a valid date, show it formatted
                                      : null // If it's the fallback default date, hide it by setting to null
                                  }
                                  onChange={(newDate) => {
                                    handleItemChange(
                                      index,
                                      "expdate",
                                      newDate
                                        ? newDate.format(GRN_DATE_FORMAT.STANDARD)
                                        : null
                                    );
                                  }}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      size: "small",
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>

                      {/* 4th qty */}
                      <TableCell sx={styles.common_cell_style_3}>
                        <Box sx={styles.grn_table_body_col_box_2} mb={0}>
                          <Box sx={styles.grn_table_body_col_box_row} gap={1}>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              required
                              disabled
                              label={ITEM_LABELS.ACCEPTED_QUANTITY}
                              value={FormatamountWithoutDecimal(
                                item.acceptedqty
                              )}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "acceptedqty",
                                  e.target.value
                                )
                              }
                              // InputProps={{
                              //   sx: { textAlign: "right" },
                              //   inputProps: { style: { textAlign: "right" } },
                              // }}
                              sx={{
                                ...styles.ipt_helper_text,
                                // ...styles.txt_grn_input_3,
                              }}
                              // error={!!errors[index]?.acceptedqty}
                              // helperText={
                              //   errors[index]?.acceptedqty
                              //     ? "Accepted Quantity is Required"
                              //     : ""
                              // }
                            />
                          </Box>
                        </Box>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={styles.grn_cell_collapse_box}>
                            <Box sx={styles.grn_table_body_col_box_row}>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                label={TABLE_HEADERS.PO_QTY}
                                value={FormatamountWithoutDecimal(item.poqty)}
                                onChange={(e) =>
                                  handleItemChange(
                                    index,
                                    "poqty",
                                    allowOnlyNumbersCommaDot(e.target.value)
                                  )
                                }
                                error={!!errors[index]?.poqty}
                                // helperText={
                                //   errors[index]?.poqty
                                //     ? GRN_ERROR_MESSAGES.PO_QTY_REQUIRED
                                //     : ""
                               
                                // }

                                // bug 7473 fixed
                                sx={styles.ipt_helper_text}
                              />
                            </Box>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              label={GRN_FORM_LABELS.PENDING_PO_QUANTITY}
                              value={FormatamountWithoutDecimal(
                                item.quantitydifference || 0
                              )}
                              disabled
                            />

                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              required
                              label={ITEM_LABELS.RECEIVED_QUANTITY}
                              value={FormatamountWithoutDecimal(
                                item.receivedqty
                              )}
                              // onChange={(e) =>
                              //   handleItemChange(
                              //     index,
                              //     "receivedqty",
                              //     e.target.value
                              //   )
                              // }
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "receivedqty",
                                  allowOnlyNumbersCommaDot(e.target.value)
                                )
                              }
                              error={!!errors[index]?.receivedqty}
                              // helperText={
                              //   errors[index]?.receivedqty
                              //     ? GRN_ERROR_MESSAGES.RECEIVED_QTY_REQUIRED
                              //     : ""
                              // }

                              // bug 7473 fixed
                              sx={styles.ipt_helper_text}
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                      {/* 5th amount */}
                      <TableCell sx={styles.common_cell_style_3}>

                        <Box sx={styles.grn_table_body_col_box_2} mb={0}>
                          <Box sx={styles.grn_table_body_col_box_row} gap={4}>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              required
                              label={ITEM_LABELS.VALUE}
                              value={FormatCurrencyLKR(item.itemvalue)}
                              disabled
                            />
                          </Box>
                        </Box>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={styles.grn_cell_collapse_box}>
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              label={ITEM_LABELS.UNIT_PRICE}
                              required
                              value={formatCurrencyForInput(item?.unitprice || '0')}
                              onFocus={(e) => {
                                // Clear the field if value is "0" or "0.00" or "0,000" etc.
                                const currentValue = parseCurrencyFromInput(e.target.value);
                                if (currentValue === '0' || currentValue === '0.0' || currentValue === '0.00') {
                                  handleItemChange(index, "unitprice", '');
                                } else {
                                  // Select all text for easy replacement
                                  e.target.select();
                                }
                              }}
                              onChange={(e) => {
                                let parsedValue = parseCurrencyFromInput(e.target.value);
                                
                                // If the value starts with "0" followed by digits (not "0.0" or "0.00"), remove leading zero
                                // This handles cases where user types while "0" is still in the field
                                if (parsedValue && parsedValue.length > 1 && parsedValue.startsWith('0') && /^0[0-9]/.test(parsedValue) && !parsedValue.startsWith('0.')) {
                                  parsedValue = parsedValue.substring(1);
                                }
                                
                                handleItemChange(
                                  index,
                                  "unitprice",
                                  parsedValue
                                );
                              }}
                              onBlur={(e) => {
                                // Ensure value is properly formatted on blur
                                const parsedValue = parseCurrencyFromInput(e.target.value);
                                let finalValue = parsedValue;
                                if (parsedValue !== '' && !isNaN(parseFloat(parsedValue))) {
                                  finalValue = parsedValue;
                                } else if (parsedValue === '' || isNaN(parseFloat(parsedValue))) {
                                  finalValue = '0';
                                }
                                
                                // Validate with current item data and the new value
                                const currentItem = grnItems[index];
                                if (currentItem) {
                                  const itemToValidate = { ...currentItem, unitprice: finalValue };
                                  const itemErrors = validateItem(itemToValidate);
                                  
                                  // Update errors state
                                  setErrors((prev: AnyForNow) => {
                                    const newErrors = { ...prev };
                                    if (itemErrors.unitprice) {
                                      newErrors[index] = { ...(newErrors[index] || {}), unitprice: true };
                                    } else if (newErrors[index]?.unitprice) {
                                      // Clear error if validation passes
                                      const updatedErrors = { ...newErrors[index] };
                                      delete updatedErrors.unitprice;
                                      if (Object.keys(updatedErrors).length === 0) {
                                        const { [index]: _, ...rest } = newErrors;
                                        return rest;
                                      } else {
                                        newErrors[index] = updatedErrors;
                                      }
                                    }
                                    return newErrors;
                                  });
                                }
                                
                                handleItemChange(index, "unitprice", finalValue);
                              }}
                              error={!!errors[index]?.unitprice}
                              // helperText={
                              //   errors[index]?.unitprice
                              //     ? GRN_ERROR_MESSAGES.UNIT_PRICE_REQUIRED
                              //     : ""
                              // }

                              // bug 7473 fixed
                              sx={styles.ipt_helper_text}
                            />
                            <TextField
                              variant="outlined"
                              size="small"
                              fullWidth
                              label={ITEM_LABELS.DISCOUNT_PERCENT}
                              inputProps={{ min: 0, max: GRN_CONSTRAINTS.MAX_PERCENTAGE, step: 0.01 }}
                              type="number"
                              value={(() => {
                                const val = String(item.discountrate || '0');
                                // Remove leading zeros for display (e.g., "010" -> "10", "02" -> "2")
                                if (val && val.length > 1 && val.startsWith('0') && !val.startsWith('0.')) {
                                  return val.replace(/^0+/, '') || '0';
                                }
                                return val;
                              })()}
                              disabled={(() => {
                                // Disable if bill-level discount is applied
                                const billLevelDiscount = Number(grnData?.billings?.billLevelDiscountPercent) || 0;
                                return billLevelDiscount > 0;
                              })()}
                              onFocus={(e) => {
                                // Clear the field if value is "0"
                                const currentValue = String(item.discountrate || '0');
                                if (currentValue === '0' || currentValue === '0.0' || currentValue === '0.00') {
                                  handleItemChange(index, "discountrate", '');
                                } else {
                                  // Select all text for easy replacement
                                  e.target.select();
                                }
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = [
                                  "Backspace",
                                  "Tab",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Delete",
                                  ".",
                                  "-",
                                  "Home",
                                  "End",
                                ];
                                const isNumberKey = /^[0-9]$/.test(e.key);
                                if (
                                  !isNumberKey &&
                                  !allowedKeys.includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                                // Prevent multiple decimals
                                if (
                                  e.key === "." &&
                                  (e.target as HTMLInputElement).value.includes(".")
                                ) {
                                  e.preventDefault();
                                }
                                // Prevent minus sign
                                if (e.key === "-" || e.key === "+") {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                let value = e.target.value;
                                
                                // Remove leading zeros (e.g., "010" becomes "10", "02" becomes "2")
                                // But preserve "0" if that's the only character, and allow "0." for decimals
                                if (value && value.length > 1) {
                                  // Remove leading zeros, but keep "0." for decimal inputs
                                  if (value.startsWith('0') && !value.startsWith('0.')) {
                                    value = value.replace(/^0+/, '') || '0';
                                    // If after removing zeros we have an empty string or just ".", keep "0"
                                    if (value === '' || value === '.') {
                                      value = '0';
                                    }
                                  }
                                }
                                
                                if (
                                  value === "" ||
                                  (Number(value) >= GRN_CONSTRAINTS.MIN_PERCENTAGE && Number(value) <= GRN_CONSTRAINTS.MAX_PERCENTAGE)
                                ) {
                                  handleItemChange(
                                    index,
                                    "discountrate",
                                    value
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                // Ensure value defaults to 0 if empty, and normalize to remove leading zeros
                                let value = e.target.value;
                                if (value === '' || isNaN(Number(value))) {
                                  handleItemChange(index, "discountrate", '0');
                                } else {
                                  // Normalize: remove leading zeros (e.g., "010" -> "10")
                                  if (value && value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
                                    value = value.replace(/^0+/, '') || '0';
                                  }
                                  // Update with normalized value
                                  const numValue = Number(value);
                                  if (!isNaN(numValue) && numValue >= GRN_CONSTRAINTS.MIN_PERCENTAGE && numValue <= GRN_CONSTRAINTS.MAX_PERCENTAGE) {
                                    handleItemChange(index, "discountrate", value);
                                  }
                                }
                              }}
                            />
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {/* Tax Rate (Editable) */}
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label={ITEM_LABELS.TAX_RATE_PERCENT}
                                type="number"
                                inputProps={{ min: 0, max: GRN_CONSTRAINTS.MAX_PERCENTAGE, step: 0.01 }}
                                value={(() => {
                                  const val = String(item.taxrate || '0');
                                  // Remove leading zeros for display (e.g., "010" -> "10", "02" -> "2")
                                  if (val && val.length > 1 && val.startsWith('0') && !val.startsWith('0.')) {
                                    return val.replace(/^0+/, '') || '0';
                                  }
                                  return val;
                                })()}
                                disabled={(() => {
                                  // Disable if bill-level tax is applied (check both grnData and payTax for edit scenarios)
                                  const billLevelTax = Number(grnData?.billings?.billLevelTaxPercent) || 0;
                                  const hasBillLevelTaxFromPayTax = payTax && payTax.length > 0 && 
                                    payTax[0]?.taxcode === TAX_CODE.BILL_LEVEL && 
                                    Number(payTax[0]?.ptaxrate || payTax[0]?.taxrate) > 0;
                                  return billLevelTax > 0 || hasBillLevelTaxFromPayTax;
                                })()}
                                onFocus={(e) => {
                                  // Clear the field if value is "0"
                                  const currentValue = String(item.taxrate || '0');
                                  if (currentValue === '0' || currentValue === '0.0' || currentValue === '0.00') {
                                    handleItemChange(index, "taxrate", '');
                                  } else {
                                    // Select all text for easy replacement
                                    e.target.select();
                                  }
                                }}
                                onChange={(e) => {
                                  let value = e.target.value;

                                  // Remove leading zeros (e.g., "010" becomes "10", "02" becomes "2")
                                  // But preserve "0" if that's the only character, and allow "0." for decimals
                                  if (value && value.length > 1) {
                                    // Remove leading zeros, but keep "0." for decimal inputs
                                    if (value.startsWith('0') && !value.startsWith('0.')) {
                                      value = value.replace(/^0+/, '') || '0';
                                      // If after removing zeros we have an empty string or just ".", keep "0"
                                      if (value === '' || value === '.') {
                                        value = '0';
                                      }
                                    }
                                  }

                                  // Allow empty, numbers, and up to 2 decimals
                                  const regex = GRN_REGEX.PERCENTAGE;

                                  if (value === "" || regex.test(value)) {
                                    // Convert to number only if not empty
                                    const numericValue = Number(value);

                                    // Enforce range (0–100)
                                    if (value === "" || (numericValue >= GRN_CONSTRAINTS.MIN_PERCENTAGE && numericValue <= GRN_CONSTRAINTS.MAX_PERCENTAGE)) {
                                      handleItemChange(index, "taxrate", value);
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  // Ensure value defaults to 0 if empty, and normalize to remove leading zeros
                                  let value = e.target.value;
                                  if (value === '' || isNaN(Number(value))) {
                                    handleItemChange(index, "taxrate", '0');
                                  } else {
                                    // Normalize: remove leading zeros (e.g., "010" -> "10", "02" -> "2")
                                    if (value && value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
                                      value = value.replace(/^0+/, '') || '0';
                                    }
                                    // Update with normalized value
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue >= GRN_CONSTRAINTS.MIN_PERCENTAGE && numValue <= GRN_CONSTRAINTS.MAX_PERCENTAGE) {
                                      handleItemChange(index, "taxrate", value);
                                    }
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Allow navigation & editing keys
                                  const allowedKeys = [
                                    "Backspace",
                                    "Tab",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Delete",
                                    ".",
                                    "Home",
                                    "End",
                                  ];
                                  const isNumberKey = /^[0-9]$/.test(e.key);

                                  // Allow Ctrl+A / Cmd+A
                                  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") return;

                                  // Prevent invalid keys
                                  if (!isNumberKey && !allowedKeys.includes(e.key)) e.preventDefault();

                                  // Prevent multiple decimals
                                  if (e.key === "." && (e.target as HTMLInputElement).value.includes(".")) e.preventDefault();
                                }}
                              />

                              {/* Tax Value (Read Only) */}
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled
                                label={ITEM_LABELS.TAX_VALUE}
                                value={FormatCurrencyLKR(item.taxvalue?.toFixed(2) || CURRENCY_CONFIG.DEFAULT_VALUE)}
                                inputProps={{ style: { textAlign: "right" } }}
                              />
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>

                      <TableCell
                        sx={{
                          maxWidth: "80px",

                          justifyContent: "center",
                          alignContent: "center",
                          py: 1,
                        }}
                      >
                         
                        <Box sx={styles.common_cell_style_2_action_box}>
                          <Tooltip title="View Item" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setPopupImage(item.imagepath);
                                setPopupName(item.productname);
                                setPopupDescription(
                                  item.supplierdescrip || "No description"
                                );
                                setImagePopupOpen(true);
                              }}
                            >
                              <ImageOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Remove item from table(added) */}

                          <Tooltip title="Remove Item" arrow>
                            <IconButton
                              size="small"
                              sx={{ color: theme.palette.system.error }}
                              onClick={() => removeItemFromTable(index)}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        
                        </Box>
                      </TableCell>

                      {/* Reject items row */}
                    </TableRow>

{expandedOptional[index] && expanded[index] &&  (
  <TableRow>
    <TableCell sx={styles.grn_table_expand_cell} colSpan={7}>
      <Box sx={styles.grn_table_expand_cell_container}>
        <Grid
          container
          spacing={2}
          sx={styles.grn_table_expand_cell_grid}
          p={1}
        >
          <Grid size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}>
            <Box sx={styles.grn_action_items_box}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label={ITEM_LABELS.BATCH_NUMBER}
                value={item.batchno}
                onChange={(e) => handleItemChange(index, "batchno", e.target.value)}
                sx={styles.ipt_helper_text}
              />
            </Box>
          </Grid>
                <Grid size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}>
            <Box sx={styles.grn_action_items_box}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label={ITEM_LABELS.SERIAL_NUMBER}
                value={item.serialno}
                onChange={(e) => {
                  const filteredValue = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                  handleItemChange(index, "serialno", filteredValue);
                }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}>
            <Box sx={styles.grn_action_items_box}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label={ITEM_LABELS.MODEL_NUMBER}
              
                 value={item.modelno === "N/A" ? "" : item.modelno}
               disabled
                sx={styles.ipt_helper_text}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2, md: 2, lg: 3, xl: 2 }}>
            <Box sx={styles.grn_action_items_box}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label={ITEM_LABELS.GENERIC_NAME}
                
                  value={item.genericname === "N/A" ? "" : item.genericname}
               disabled
                // onChange={(e) => handleItemChange(index, "genericno", e.target.value)}
                sx={styles.ipt_helper_text}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2, md: 2, lg: 3, xl: 2 }}>
            <Box sx={styles.grn_action_items_box}>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                label={ITEM_LABELS.PACKAGE_NAME}
                value={item.packagename === "N/A" ? "" : item.packagename}
             
                sx={styles.ipt_helper_text}
                disabled
              />
            </Box>
          </Grid>
    
        </Grid>
      </Box>
      <Divider />
    </TableCell>
  </TableRow>
)}
                    <TableRow>
                      <TableCell sx={styles.grn_table_expand_cell} colSpan={7}>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box>
                            <Grid
                              container
                              spacing={2}
                              sx={styles.grn_table_expand_cell_grid}
                            >
                              <Grid
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 12,
                                  lg: 12,
                                  xl: 12,
                                }}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  sx={styles.btn_text}
                                  // onClick={() => toggleRejectItems(index)}
                                  onClick={() => toggleRejectItems(item.id)}
                                >
                                  {" "}
                                  {ITEM_LABELS.REJECT_ITEMS}
                                </Button>
                                <Button
                                  sx={styles.btn_text}
                                  // onClick={() => toggleFocItems(index)}
                                  onClick={() => toggleFocItems(item.id)}
                                >
                                  {ITEM_LABELS.FOC_ITEMS}
                                </Button>
                                <Button
                                  sx={styles.btn_text}
                                  onClick={() => toggleItemAttachments(item.id)}

                                  // onClick={() => toggleItemAttachments(index)}
                                >
                                  {ITEM_LABELS.ATTACHMENTS}
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                          <Divider />
                        </Collapse>
                      </TableCell>
                    </TableRow>



                    {showRejectItems?.[item.id] && (
                      <TableRow>
                        <TableCell
                          sx={styles.grn_table_expand_cell}
                          colSpan={7}
                        >
                          <Collapse
                            in={expanded?.[index]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={styles.grn_table_expand_cell_container}>
                              <Box
                                sx={
                                  styles.grn_table_expand_cell_container_title
                                }
                              >
                                <Typography sx={styles.grn_table_row}>
                                  {ITEM_LABELS.REJECT_ITEMS}
                                </Typography>
                              </Box>
                              <Grid
                                container
                                spacing={2}
                                sx={styles.grn_table_expand_cell_grid}
                                p={1}
                              >
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <TextField
                                      fullWidth
                                      id="po-reference-number"
                                      label={ITEM_LABELS.REJECT_QUANTITY}
                                      variant="outlined"
                                      size="small"
                                      value={item.rejectItem?.returnedqty || ""}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const poQty = Number(item.poqty) || 0;
                                        const enteredValue = Number(value) || 0;

                                        if (enteredValue > poQty) {
                                          setAlertType("error");
                                          setAlertMessage(`Reject quantity cannot exceed PO quantity (${poQty})`);
                                          setAlertOpen(true);
                                          return;
                                        }

                                        handleRejectItemChange(
                                          index,
                                          "returnedqty",
                                          value
                                        );
                                      }}
                                    />
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <FormControl
                                      variant="outlined"
                                      fullWidth
                                      size="small"
                                    >
                                      <InputLabel id={`reason-label-${index}`}>{ITEM_LABELS.REASON}</InputLabel>
                                      <Select
                                        labelId={`reason-label-${index}`}
                                        value={item.rejectItem?.reason || ""}
                                        onChange={(e) =>
                                          handleRejectItemChange(index, "reason", e.target.value)
                                        }
                                        label={ITEM_LABELS.REASON}
                                        disabled={reasonLoading}
                                        MenuProps={{
                                          PaperProps: {
                                            sx: {
                                              boxShadow: 2, // shadow
                                              // borderRadius: 2, // enable if you want rounded dropdown
                                            },
                                          },
                                          anchorOrigin: {
                                            vertical: "bottom",
                                            horizontal: "left",
                                          },
                                          transformOrigin: {
                                            vertical: "top",
                                            horizontal: "left",
                                          },
                                        }}
                                      >
                                        {reasonLoading ? (
                                          <MenuItem disabled>Loading...</MenuItem>
                                        ) : reasonOptions.length === 0 ? (
                                          <MenuItem disabled>No reasons available</MenuItem>
                                        ) : (
                                          reasonOptions.map((option: AnyForNow, idx: number) => {
                                            const value = option.descrip || option.reasontypecode || option;
                                            const displayText = option.descrip || option.reasontypecode || String(option);
                                            return (
                                              <MenuItem key={idx} value={value}>
                                                {displayText}
                                              </MenuItem>
                                            );
                                          })
                                        )}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <TextField
                                      fullWidth
                                      id="po-reference-number"
                                      label={ITEM_LABELS.REMARK}
                                      variant="outlined"
                                      size="small"
                                      value={item.rejectItem?.descrip || ""}
                                      onChange={(e) =>
                                        handleRejectItemChange(
                                          index,
                                          "descrip",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}
                                >
                                  <Box sx={{ ...styles.grn_action_items_box, width: '100%', '& > div': { width: '100%' } }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Upload
                                        MAX_FILESIZE={GRN_CONSTRAINTS.MAX_FILE_SIZE}
                                        setSnackbarMessage={() => {}}
                                        setOpenErrorSnackbar={() => {}}
                                        setFilePath={() => {}}
                                        onFileSelect={async (
                                          fileName: string,
                                          base64DataURL: string,
                                          rawFile: File
                                        ) => {
                                          setRejectAttachmentUploadingIndex(index);
                                          const base64Only =
                                            base64DataURL.split(",")[1];
                                          let uploadedPath = "";

                                          try {
                                            uploadedPath = await uploadAttachmentFile(rawFile);
                                          } catch (error) {
                                            console.error("Reject item upload failed", error);
                                          } finally {
                                            setRejectAttachmentUploadingIndex(null);
                                          }

                                          const usePathOnly = Boolean(uploadedPath && uploadedPath.trim());
                                          const newAttachment = {
                                            doctypecode:
                                              item.rejectItem?.doctypecode || "",
                                            docpathjson: usePathOnly ? uploadedPath.trim() : base64Only,
                                            filename: fileName,
                                            bytarr: usePathOnly ? [] : base64Only,
                                            objectUrl: "",
                                            fileUrl: uploadedPath,
                                            FILEPATH: uploadedPath,
                                            remark: item.rejectItem?.remark || "",
                                            isNew: true,
                                          };

                                          handleRejectAttachmentAdd(
                                            index,
                                            newAttachment
                                          );
                                          setRejectFileName("");
                                        }}
                                        fileName={rejectFileName}
                                        setFileName={setRejectFileName}
                                        objectUrl=""
                                        resetKey={attachmentsToShow.length}
                                      />
                                    </Box>

                                    {rejectAttachmentUploadingIndex === index ? (
                                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                                        <CircularProgress size={16} />
                                        <Typography
                                          sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: "12px",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          Uploading...
                                        </Typography>
                                      </Box>
                                    ) : attachmentsToShow.length === 0 ? (
                                      <Typography
                                        sx={{
                                          color: theme.palette.text.secondary,
                                          fontSize: "12px",
                                          fontStyle: "italic",
                                          mt: 1,
                                        }}
                                      >
                                        No attachment available
                                      </Typography>
                                    ) : (
                                      attachmentsToShow.map(
                                        (attachment: AnyForNow, attIndex: number) => {
                                          const fileName = attachment?.filename || attachment?.FILENAME || "";
                                          const filePath = attachment?.FILEPATH || attachment?.fileUrl || "";
                                          const fileNameForType = fileName || filePath;
                                          const isImg = isImageFile(fileNameForType);
                                          const isPdf = fileNameForType.toLowerCase().endsWith(".pdf");

                                          return (
                                        <Box
                                          key={attIndex}
                                          display="flex"
                                          flexDirection="row"
                                          alignItems="center"
                                          gap={1}
                                          mt={0}
                                        >
                                          {isImg ? (
                                            <Tooltip title={fileName} placement="bottom">
                                              <Box sx={{ position: "relative", display: "inline-block",  filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none", }}>
                                                <img
                                                  src={getAttachmentSrc(attachment, "image")}
                                                  alt={fileName || "Attachment"}
                                                  style={{
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: "cover",
                                                    cursor: "pointer",
                                                    borderRadius: "2px",
                                                    display: "block",
                                                    
                                                  }}
                                                  onClick={() => {
                                                    setSelectedImage({
                                                      url: getAttachmentSrc(attachment, "image"),
                                                      name: fileName,
                                                      description: attachment?.REMARK || "",
                                                    });
                                                    setImagePreviewOpen(true);
                                                  }}
                                                />

                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  sx={{
                                                    position: "absolute",
                                                    top: -8,
                                                    right: -8,
                                                    width: 18,
                                                    height: 18,
                                                    backgroundColor: "white",
                                                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
                                                    "&:hover": {
                                                      backgroundColor: "white",
                                                    },
                                                  }}
                                                  onClick={() =>
                                                    handleRejectAttachmentRemove(
                                                      index,
                                                      attachment.id
                                                    )
                                                  }
                                                >
                                                  <ClearIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </Box>
                                            </Tooltip>
                                          ) : isPdf ? (
                                            <Tooltip title={fileName} placement="bottom">
                                              <Box sx={{ position: "relative", display: "inline-block" }}>
                                                <Box
                                                  onClick={() => {
                                                    setSelectedImage({
                                                      url: getAttachmentSrc(attachment, "pdf"),
                                                      name: fileName,
                                                      description: attachment?.REMARK || "",
                                                      filepath: "",
                                                      objectUrl: attachment?.objectUrl || "",
                                                    });
                                                    setImagePreviewOpen(true);
                                                  }}
                                                  sx={{
                                                    width: 40,
                                                    height: 40,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#f0f0f0",
                                                    borderRadius: "2px",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <Typography fontSize="10px" textAlign="center" fontWeight={600}>
                                                    PDF
                                                  </Typography>
                                                </Box>
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  sx={{
                                                    position: "absolute",
                                                    top: -8,
                                                    right: -8,
                                                    width: 18,
                                                    height: 18,
                                                    backgroundColor: "white",
                                                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
                                                    zIndex: 100,
                                                    "&:hover": {
                                                      backgroundColor: "white",
                                                    },
                                                  }}
                                                  onClick={() =>
                                                    handleRejectAttachmentRemove(
                                                      index,
                                                      attachment?.id || attIndex
                                                    )
                                                  }
                                                >
                                                  <ClearIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </Box>
                                            </Tooltip>
                                          ) : (
                                            <Tooltip title={fileName} placement="bottom">
                                              <Box sx={{ position: "relative", display: "inline-block" }}>
                                                <Box
                                                  onClick={() => {
                                                    setSelectedImage({
                                                      url: getAttachmentSrc(attachment, "octet"),
                                                      name: fileName,
                                                      description: attachment?.REMARK || "",
                                                      filepath: "",
                                                      objectUrl: attachment?.objectUrl || "",
                                                    });
                                                    setImagePreviewOpen(true);
                                                  }}
                                                  sx={{
                                                    width: 40,
                                                    height: 40,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: "#e0e0e0",
                                                    borderRadius: "2px",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <AttachFileIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                                                </Box>
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  sx={{
                                                    position: "absolute",
                                                    top: -8,
                                                    right: -8,
                                                    width: 18,
                                                    height: 18,
                                                    backgroundColor: "white",
                                                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
                                                    zIndex: 100,
                                                    "&:hover": {
                                                      backgroundColor: "white",
                                                    },
                                                  }}
                                                  onClick={() =>
                                                    handleRejectAttachmentRemove(
                                                      index,
                                                      attachment?.id || attIndex
                                                    )
                                                  }
                                                >
                                                  <ClearIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </Box>
                                            </Tooltip>
                                          )}
                                        </Box>
                                      );
                                      }
                                    )
                                    )}
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_table_expand_cell_action}>
                                    <IconButton
                                      size="small"
                                      sx={{ color: theme.palette.system.error }}
                                      onClick={() =>
                                        handleClearRejectItems(item.id, index)
                                      }
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                            <Divider />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}

                    {showFocItems?.[item.id] && (
                      <TableRow>
                        <TableCell
                          sx={styles.grn_table_expand_cell}
                          colSpan={7}
                        >
                          <Collapse
                            in={expanded?.[index]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={styles.grn_table_expand_cell_container}>
                              <Box
                                sx={
                                  styles.grn_table_expand_cell_container_title
                                }
                              >
                                <Typography sx={styles.grn_table_row}>
                                  {ITEM_LABELS.FOC_ITEMS}
                                </Typography>
                              </Box>
                              <Grid
                                container
                                spacing={2}
                                sx={styles.grn_table_expand_cell_grid}
                                p={1}
                              >
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <TextField
                                      fullWidth
                                      id="po-reference-number"
                                      label={ITEM_LABELS.FOC_QUANTITY}
                                      variant="outlined"
                                      size="small"
                                      value={item.freeqty}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "freeqty",
                                          e.target.value
                                        )
                                      }
                                      // required
                                    />
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <TextField
                                      fullWidth
                                      id="po-reference-number"
                                      label="Remark"
                                      variant="outlined"
                                      size="small"
                                      value={item.descrip}
                                      onChange={(e) =>
                                        handleItemChange(
                                          index,
                                          "descrip",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Box>
                                </Grid>{" "}
                                <Grid
                                  size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }}
                                >
                                  <Box sx={styles.grn_table_expand_cell_action}>
                                    {/* <IconButton
                                    size="small"
                                    sx={{
                                      color: (theme.palette as any).greyscale.icons,
                                    }}
                                  >
                                    <ModeEditOutlineRoundedIcon fontSize="small" />
                                  </IconButton> */}
                                    <IconButton
                                      size="small"
                                      sx={{ color: theme.palette.system.error }}
                                      onClick={() =>
                                        handleClearFocItems(item.id, index)
                                      }
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                            <Divider />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}

                    {showItemAttachments?.[item.id] && (
                      <TableRow>
                        <TableCell
                          sx={styles.grn_table_expand_cell}
                          colSpan={7}
                        >
                          <Collapse
                            in={expanded?.[index]}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={styles.grn_table_expand_cell_container}>
                              <Box
                                sx={
                                  styles.grn_table_expand_cell_container_title
                                }
                              >
                                <Typography sx={styles.grn_table_row}>
                                  {ITEM_LABELS.ATTACHMENTS}
                                </Typography>
                              </Box>
                              <Grid
                                container
                                spacing={2}
                                sx={styles.grn_table_expand_cell_grid}
                                p={1}
                              >
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>{ITEM_LABELS.DOCUMENT_TYPE}</InputLabel>
                                      <Select
                                        label={ITEM_LABELS.DOCUMENT_TYPE}
                                        value={
                                          item.selectedDocumentTypeCode ||
                                          (grnDocTypes.find(
                                            (doc: AnyForNow) =>
                                              doc.doctypecode ===
                                                ((item.itemAttachments as any)?.attachments?.[0]?.doctypecode || "") ||
                                              doc.doctype ===
                                                ((item.itemAttachments as any)?.attachments?.[0]?.doctype || "")
                                          )?.doctypecode) ||
                                          (() => {
                                            try {
                                              const arr = JSON.parse(item?.docpathjson || "[]");
                                              const att = Array.isArray(arr) ? arr[0] : null;
                                              if (!att) return "";
                                              const byCode = grnDocTypes.find(
                                                (d: AnyForNow) => String(d.doctypecode) === String(att.DOCTYPECODE || "")
                                              );
                                              const byName = grnDocTypes.find(
                                                (d: AnyForNow) => String(d.doctype) === String(att.DOCTYPE || "")
                                              );
                                              return (byCode?.doctypecode || byName?.doctypecode || "");
                                            } catch (e) {
                                              return "";
                                            }
                                          })() ||
                                          ""
                                        }
                                        onChange={(e) => {
                                          const selectedValue = e.target.value;
                                          handleItemChange(index, "selectedDocumentTypeCode", selectedValue);
                                        }}
                                        MenuProps={{
                                          PaperProps: {
                                            sx: { boxShadow: 2 },
                                          },
                                        }}
                                      >
                                        {grnDocTypes.map((doc: AnyForNow) => (
                                          <MenuItem key={doc.doctypecode} value={doc.doctypecode}>
                                            {doc.doctype}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Grid>

                                <Grid
                                  size={{
                                    xs: 12,
                                    sm: 2,
                                    md: 2,
                                    lg: 2,
                                    xl: 2,
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    label={ITEM_LABELS.CUSTOM_DOCUMENT_TYPE}
                                    size="small"
                                    value={
                                      item.doctypeothername ||
                                      (item.itemAttachments as any)?.attachments?.[0]?.doctypeothername ||
                                      (() => {
                                        try {
                                          const arr = JSON.parse(item?.docpathjson || "[]");
                                          const att = Array.isArray(arr) ? arr[0] : null;
                                          return att?.DOCTYPEOTHERNAME || "";
                                        } catch (e) {
                                          return "";
                                        }
                                      })() ||
                                      ""
                                    }
                                    onChange={(e) => handleItemChange(index, "doctypeothername", e.target.value)}
                                    disabled={(() => {
                                      const selectedDocTypeCode = item.selectedDocumentTypeCode ||
                                        (grnDocTypes.find(
                                          (doc: AnyForNow) =>
                                            doc.doctypecode ===
                                              ((item.itemAttachments as any)?.attachments?.[0]?.doctypecode || "") ||
                                            doc.doctype ===
                                              ((item.itemAttachments as any)?.attachments?.[0]?.doctype || "")
                                        )?.doctypecode) ||
                                        (() => {
                                          try {
                                            const arr = JSON.parse(item?.docpathjson || "[]");
                                            const att = Array.isArray(arr) ? arr[0] : null;
                                            if (!att) return "";
                                            const byCode = grnDocTypes.find(
                                              (d: AnyForNow) => String(d.doctypecode) === String(att.DOCTYPECODE || "")
                                            );
                                            const byName = grnDocTypes.find(
                                              (d: AnyForNow) => String(d.doctype) === String(att.DOCTYPE || "")
                                            );
                                            return (byCode?.doctypecode || byName?.doctypecode || "");
                                          } catch (e) {
                                            return "";
                                          }
                                        })() ||
                                        "";
                                      const selected = grnDocTypes.find((doc: AnyForNow) => doc.doctypecode === selectedDocTypeCode);
                                      return (selected?.doctype || "") !== "Other";
                                    })()}
                                  />
                                </Grid>

                                {/* {documentType === "Other" && ( */}
                                {/* <Grid
                                  size={{
                                    xs: 12,
                                    sm: 2,
                                    md: 2,
                                    lg: 2,
                                    xl: 2,
                                  }}
                                >
                                  <TextField
                                    fullWidth
                                    label="Document Name"
                                    size="small"
                                    disabled={documentType !== "Other"}
                                  />
                                </Grid> */}
                                {/* )} */}
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_action_items_box}>
                                    <TextField
                                      fullWidth
                                      label={ITEM_LABELS.REMARK}
                                      variant="outlined"
                                      size="small"
                                      value={
                                        (item.itemAttachments as any)?.attachments?.[0]?.remark ||
                                        (item.itemAttachments as any)?.attachments?.[0]?.REMARK ||
                                        (() => {
                                          try {
                                            const arr = JSON.parse(item?.docpathjson || "[]");
                                            const att = Array.isArray(arr) ? arr[0] : null;
                                            return att?.REMARK || "";
                                          } catch (e) {
                                            return "";
                                          }
                                        })() ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleItemAttachmentChange(index, "remark", e.target.value)
                                      }
                                    />
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}
                                >
                                  <Box sx={{ ...styles.grn_action_items_box, width: '100%', '& > div': { width: '100%' } }}>
                                    <Box sx={{ width: '100%' }}>
                                      <Upload
                                        MAX_FILESIZE={GRN_CONSTRAINTS.MAX_FILE_SIZE}
                                        setSnackbarMessage={() => {}}
                                        setOpenErrorSnackbar={() => {}}
                                        setFilePath={() => {}}
                                        onFileSelect={async (
                                          fileName: string,
                                          base64DataURL: string,
                                          rawFile: File
                                        ) => {
                                          setItemAttachmentUploadingIndex(index);
                                          const base64Only =
                                            base64DataURL.split(",")[1];
                                          let uploadedPath = "";

                                          try {
                                            uploadedPath = await uploadAttachmentFile(rawFile);
                                          } catch (error) {
                                            console.error("Item attachment upload failed", error);
                                          } finally {
                                            setItemAttachmentUploadingIndex(null);
                                          }

                                          handleItemAttachmentFileUpload(
                                            {
                                              base64: base64Only,
                                              fileName: fileName,
                                              objectUrl: "",
                                              fileUrl: uploadedPath,
                                              byteArray: "",
                                              doctypecode:
                                                item.selectedDocumentTypeCode ||
                                                (item.itemAttachments as any)?.attachments?.[0]?.doctypecode ||
                                                "",
                                              doctypeothername: item.doctypeothername || ""
                                            },
                                            index
                                          );
                                          setAttachmentFileName("");
                                        }}
                                        fileName={attachmentFileName}
                                        setFileName={setAttachmentFileName}
                                        objectUrl=""
                                        resetKey={validAttachments.length}
                                      />
                                    </Box>

                                    {itemAttachmentUploadingIndex === index ? (
                                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                                        <CircularProgress size={16} />
                                        <Typography
                                          sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: "12px",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          Uploading...
                                        </Typography>
                                      </Box>
                                    ) : !Array.isArray(validAttachments) || validAttachments.length === 0 ? (
                                      <Typography
                                        sx={{
                                          color: theme.palette.text.secondary,
                                          fontSize: "12px",
                                          fontStyle: "italic",
                                          mt: 1,
                                        }}
                                      >
                                        No attachment available
                                      </Typography>
                                    ) : (
                                      validAttachments.map(
                                        (attachment: AnyForNow, attIndex: number) => {
                                          const fileName = attachment.FILENAME || attachment.filename || "";
                                          const filePath = attachment.FILEPATH || attachment.fileUrl || "";
                                          const fileNameForType = fileName || filePath;
                                          const isImg = isImageFile(fileNameForType);
                                          const isPdf = fileNameForType.toLowerCase().endsWith(".pdf");

                                          return (
                                          <Box
                                            key={attIndex}
                                            display="flex"
                                            flexDirection="row"
                                            alignItems="center"
                                            gap={1}
                                            mt={0}
                                          >
                                            {isImg ? (
                                              <Tooltip
                                                title={fileName}
                                                placement="bottom"
                                              >
                                                <Box sx={{ position: "relative", display: "inline-block",  filter: (theme) =>
      theme.palette.mode === "dark"
        ? "invert(1)"
        : "none", }}>
                                                  <img
                                                    src={getAttachmentSrc(attachment, "image")}
                                                    alt={fileName}
                                                    style={{
                                                      width: 40,
                                                      height: 40,
                                                      objectFit: "cover",
                                                      cursor: "pointer",
                                                      borderRadius: "2px",
                                                      display: "block",
                                                    }}
                                                    onClick={() => {
                                                      setSelectedImage({
                                                        url: getAttachmentSrc(attachment, "image"),
                                                        name: fileName,
                                                        description: attachment.REMARK || "",
                                                        objectUrl: attachment?.objectUrl || "",
                                                      });
                                                      setImagePreviewOpen(true);
                                                    }}
                                                  />
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    sx={{
                                                      position: "absolute",
                                                      top: -8,
                                                      right: -8,
                                                      width: 18,
                                                      height: 18,
                                                      backgroundColor: "white",
                                                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
                                                      "&:hover": {
                                                        backgroundColor: "white",
                                                      },
                                                    }}
                                                    onClick={() =>
                                                      handleItemAttachmentRemove(
                                                        index,
                                                        attachment
                                                      )
                                                    }
                                                  >
                                                    <ClearIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Box>
                                              </Tooltip>
                                            ) : isPdf ? (
                                              <Tooltip title={fileName} placement="bottom">
                                                <Box sx={{ position: "relative", display: "inline-block" }}>
                                                  <Box
                                                    onClick={() => {
                                                      setSelectedImage({
                                                        url: getAttachmentSrc(attachment, "pdf"),
                                                        name: fileName,
                                                        description: attachment.REMARK || "",
                                                        objectUrl: attachment?.objectUrl || "",
                                                      });
                                                      setImagePreviewOpen(true);
                                                    }}
                                                    sx={{
                                                      width: 40,
                                                      height: 40,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      backgroundColor: "#f0f0f0",
                                                      borderRadius: "2px",
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    <Typography fontSize="10px" textAlign="center" fontWeight={600}>
                                                      PDF
                                                    </Typography>
                                                  </Box>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    sx={{
                                                      position: "absolute",
                                                      top: -8,
                                                      right: -8,
                                                      width: 18,
                                                      height: 18,
                                                      backgroundColor: "white",
                                                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
                                                      zIndex: 100,
                                                      "&:hover": {
                                                        backgroundColor: "white",
                                                      },
                                                    }}
                                                    onClick={() =>
                                                      handleItemAttachmentRemove(
                                                        index,
                                                        attachment
                                                      )
                                                    }
                                                  >
                                                    <ClearIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Box>
                                              </Tooltip>
                                            ) : (
                                              <Tooltip title={fileName} placement="bottom">
                                                <Box sx={{ position: "relative", display: "inline-block" }}>
                                                  <Box
                                                    onClick={() => {
                                                      setSelectedImage({
                                                        url: getAttachmentSrc(attachment, "octet"),
                                                        name: fileName,
                                                        description: attachment.REMARK || "",
                                                        objectUrl: attachment?.objectUrl || "",
                                                      });
                                                      setImagePreviewOpen(true);
                                                    }}
                                                    sx={{
                                                      width: 40,
                                                      height: 40,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      backgroundColor: "#e0e0e0",
                                                      borderRadius: "2px",
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    <AttachFileIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                                                  </Box>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    sx={{
                                                      position: "absolute",
                                                      top: -8,
                                                      right: -8,
                                                      width: 18,
                                                      height: 18,
                                                      backgroundColor: "white",
                                                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
                                                      zIndex: 100,
                                                      "&:hover": {
                                                        backgroundColor: "white",
                                                      },
                                                    }}
                                                    onClick={() =>
                                                      handleItemAttachmentRemove(
                                                        index,
                                                        attachment
                                                      )
                                                    }
                                                  >
                                                    <ClearIcon sx={{ fontSize: 14 }} />
                                                  </IconButton>
                                                </Box>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        );
                                        }
                                      )
                                    )}
                                  </Box>
                                </Grid>
                                <Grid
                                  size={{ xs: 12, sm: 2, md: 2, lg: 2, xl: 2 }}
                                >
                                  <Box sx={styles.grn_table_expand_cell_action}>
                                    {/* <IconButton
                                    size="small"
                                    sx={{
                                      color: (theme.palette as any).greyscale.icons,
                                    }}
                                  >
                                    <ModeEditOutlineRoundedIcon fontSize="small" />
                                  </IconButton> */}
                                    <IconButton
                                      size="small"
                                      sx={{ color: theme.palette.system.error }}
                                      onClick={() =>
                                        handleClearAttachments(item.id)
                                      }
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                            <Divider />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={styles.grn_table_expand_cell} colSpan={7}>
                        <Collapse
                          in={expanded[index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              p: 1,
                            }}
                          >
                            <Grid
                              container
                              spacing={2}
                              sx={styles.grn_table_expand_cell_grid}
                            >
                              <Grid
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 12,
                                  lg: 12,
                                  xl: 12,
                                }}
                              >
                                <Box
                                  gap={1}
                                  sx={styles.grn_table_expand_cell_action}
                                >
                                  {/* <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ ...styles.btn_secondary }}
                                  startIcon={<EditIcon />}
                                >
                                  Edit{" "}
                                </Button> */}
                                  <Button
                                    size="small"
                                    variant="contained"
                                    sx={{ ...styles.btn_primary }}
                                    onClick={() => handleAddItem(index)}
                                  >
                                    {BUTTON_LABELS.ADD}
                                  </Button>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                          <Divider />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          <ImagePopUp
            open={imagePopupOpen}
            handleClose={() => setImagePopupOpen(false)}
            image={popupImage}
            name={popupName}
            description={popupDescription}
          />

          <ImagePopUp
            open={imagePreviewOpen}
            handleClose={() => setImagePreviewOpen(false)}
            image={selectedImage?.url}
            name={selectedImage?.name}
            description={selectedImage?.description}
            objectUrl={selectedImage?.objectUrl}
          />
        </TableContainer>
      </Collapse>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders
// Only re-render when grnItems actually changes, not when grnData changes
const MemoizedGrnItemsTable = React.memo(GrnItemsTable, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)

  // Compare grnItems by reference - this is the main data source
  if (prevProps.grnItems !== nextProps.grnItems) return false;

  // Compare essential state props
  if (prevProps.errors !== nextProps.errors) return false;
  if (prevProps.inputValue !== nextProps.inputValue) return false;
  if (prevProps.supplierItems !== nextProps.supplierItems) return false;

  // Compare show states by stringification (objects)
  if (JSON.stringify(prevProps.showRejectItems) !== JSON.stringify(nextProps.showRejectItems)) return false;
  if (JSON.stringify(prevProps.showFocItems) !== JSON.stringify(nextProps.showFocItems)) return false;
  if (JSON.stringify(prevProps.showItemAttachments) !== JSON.stringify(nextProps.showItemAttachments)) return false;

  // Skip comparing grnData - we now use grnItems directly
  // Skip comparing function props - they may have new references but shouldn't trigger re-render

  return true; // Props are equal, skip re-render
});

export default MemoizedGrnItemsTable;
